import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, Info, Network, Pill, ShieldAlert, Syringe } from "lucide-react";
import { formatDate } from "@/utils/formatDate";
import { PageHeader } from "@/components/PageHeader";
import { NeonCard } from "@/components/NeonCard";
import { listPublishedPosts, type PublicPost } from "@/lib/public-content.functions";
import { buildMeta } from "@/lib/seo";
import { PageSources } from "@/components/SourcesSheet";
import { errorMessage } from "@/lib/error-capture";

export const Route = createFileRoute("/harm-reduction")({
  head: () =>
    buildMeta({
      title: "Harm Reduction",
      description:
        "Safer-Use-Guides, Interaktions-Checker und praxisnahe Informationen zur Schadensminimierung bei psychoaktiven Substanzen.",
      path: "/harm-reduction",
    }),

  loader: () => listPublishedPosts({ data: { type: "harmreduction" } }),
  errorComponent: ({ error }) => <div role="alert" style={{ padding: "2rem", color: "var(--text-secondary)" }}>Fehler: {errorMessage(error)}</div>,
  notFoundComponent: () => <div style={{ padding: "2rem" }}>Keine Artikel gefunden.</div>,
  component: HarmReduction,
});

function HarmReduction() {
  const items = Route.useLoaderData();

  return (
    <div data-section="hr">
      <PageHeader icon={<ShieldAlert size={28} />} title="Harm Reduction" />

      <nav
        aria-label="Harm Reduction Werkzeuge"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.5rem",
          marginBottom: "1.5rem",
        }}
      >
        <ToolBarItem to="/matrix" icon={<Network size={16} />} label="Interaktion" section="matrix" />
        <ToolBarItem to="/benzo" icon={<Pill size={16} />} label="Benzodiazepine" section="benzo" />
        <ToolBarItem to="/opioide" icon={<Syringe size={16} />} label="Opioide" section="opioide" />
        <ToolBarItem to="/notfall" icon={<AlertTriangle size={16} />} label="Notfall" section="hr" />
      </nav>


      <div className="posts-grid">
        {items.map((p: PublicPost) => (
          <NeonCard as="article" key={p.id} style={{ padding: "1.5rem", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>{formatDate(p.published_date)}</span>
              <span
                style={{
                  background: "var(--accent-bg)",
                  color: "var(--accent)",
                  border: "1px solid var(--accent-border)",
                  borderRadius: "20px",
                  padding: "0.2rem 0.6rem",
                  fontSize: "0.8rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Harm Reduction
              </span>
            </div>
            <h2 style={{ fontSize: "1.15rem", fontWeight: 700, color: "var(--text-primary)", marginTop: "0.5rem" }}>
              {p.title}
            </h2>
            <p style={{ color: "var(--text-secondary)", fontSize: "0.9rem", marginTop: "0.4rem", flex: 1 }}>{p.excerpt}</p>
            <Link
              to="/harm-reduction/$slug"
              params={{ slug: p.slug }}
              style={{ color: "var(--accent)", fontSize: "0.85rem", marginTop: "1rem", textDecoration: "none" }}
            >
              Weiterlesen →
            </Link>
          </NeonCard>
        ))}
      </div>
      <NeonCard
        style={{
          padding: "1rem 1.5rem",
          marginTop: "2rem",
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
        }}
      >
        <Info size={16} style={{ color: "var(--accent)", flexShrink: 0 }} />
        <span style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
          Diese Seite dient der Schadensminimierung und ersetzte keine medizinische Beratung.
        </span>
      </NeonCard>
      <PageSources path="/harm-reduction" />
    </div>
  );
}

function ToolBarItem({ to, icon, label, section }: { to: string; icon: React.ReactNode; label: string; section: "matrix" | "benzo" | "opioide" | "hr" }) {
  return (
    <div data-section={section} style={{ display: "inline-flex" }}>
      <NeonCard
        as={Link}
        to={to}
        interactive
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "0.55rem 0.95rem",
          textDecoration: "none",
          color: "var(--accent)",
          fontWeight: 600,
          fontSize: "0.9rem",
          lineHeight: 1.2,
        }}
      >
        {icon}
        <span>{label}</span>
      </NeonCard>
    </div>
  );
}




